/**
 * `analyserTexteLatex(texte) → SegmentMath[]` — l'entrée LaTeX du moteur.
 *
 * Le fondateur a tranché (document 22, « L'écriture des mathématiques ») :
 * toutes les mathématiques s'écrivent désormais en LaTeX RESTREINT, entre
 * `\(` et `\)`, au milieu de la prose — à la lecture des photos (Mathpix et
 * les modèles de vision écrivent déjà ainsi), dans les retours des modèles,
 * dans les exercices de la banque.
 *
 * Cela ne contredit pas `expression.ts` (« pourquoi un arbre et pas du
 * LaTeX ») : le LaTeX devient l'ÉCRITURE, l'arbre reste le CONTRAT. Rien
 * n'est jamais exécuté. Chaque commande d'une liste FERMÉE est traduite en
 * nœuds existants ; tout ce qui sort de la liste fait retomber le segment
 * ENTIER en prose, brut, et le signale (`diagnostiquerLatex`). C'est la
 * discipline d'`analyser.ts` appliquée à une autre écriture : mieux vaut le
 * texte brut qu'un rendu qui affirme autre chose.
 *
 * ═══ Deux différences voulues avec `analyserTexteMath` ═══
 *
 *  1. Rien n'est DEVINÉ. L'auteur a délimité ses mathématiques : pas de runs
 *     à repérer, pas de dates à protéger, pas de `n/a` à écarter. Hors de
 *     `\( … \)`, tout est prose, `2/3` compris.
 *  2. Tout segment réussi est une EXPRESSION, même `\(x + 1\)` qui tiendrait
 *     sur une ligne de texte : sa source est du LaTeX, et la servir comme
 *     prose montrerait des barres obliques inverses à l'élève. L'appelant qui
 *     voudrait composer ces cas-là en texte dispose d'`exigeDeuxDimensions`
 *     — il part alors de l'arbre, jamais de la source.
 *
 * ═══ Le rendu suit LaTeX, pas l'algèbre ═══
 *
 * LaTeX ne décrit que la DISPOSITION : `\(a + b \cdot c\)` est une rangée
 * plate, et seules les structures explicites (`\frac`, `\sqrt`, scripts,
 * délimiteurs) créent de la profondeur. Aucune priorité d'opérateur n'est
 * donc à trancher ici — c'est l'auteur qui a posé ses accolades. Un segment
 * tronqué comme `\(-7,1 \in\)` se dessine tel qu'il est écrit, parce que
 * c'est exactement ce qu'il affirme.
 *
 * Invariant, comme `analyserTexteMath` : la concaténation des `texte` de
 * prose et des `source` d'expression redonne le texte d'origine, à
 * l'identique. La `source` d'une expression porte ses délimiteurs `\(` `\)`.
 */
import { disposerMath } from './disposer.js';
import { atome, operateur, rangee, sansCible } from './expression.js';
/* ═════════════════════════════════════════════════════════ la liste */
/**
 * Les commandes permises — la table du document 22, plus ce que la commande
 * du chantier y ajoute explicitement : les abréviations `\le` `\ge` `\ne`,
 * `\mathrm` (Mathpix l'écrit pour les unités là où un modèle écrit `\text`),
 * et les espaces. `\circ` a DEUX emplois, que sa place distingue : en exposant
 * (`90^{\circ}`, `^\circ`), c'est le degré ; entre deux fonctions
 * (`f \circ g`, tranche T29), c'est la composition — « f rond g ».
 *
 * ═══ Ce que l'école écrit, et que la liste refusait (12 septembre) ═══
 *
 * Relevé sur iPad, dans une copie corrigée : `\to`, `\Rightarrow`, `\quad`,
 * `\ne`, `\Delta`. Refusés, donc rendus en SOURCE à l'élève — « corrige avec
 * \(1\to a\) » s'affichait tel quel. Ce ne sont pas des commandes de mise en
 * page : ce sont les notations du programme québécois (une correspondance,
 * une implication, une variation), et un correcteur les écrit sans y penser.
 *
 * Trois familles entrent donc, et rien d'autre :
 *
 *  - les FLÈCHES (`\to` `\rightarrow` `\Rightarrow` `\Leftrightarrow`
 *    `\mapsto`), posées en opérateurs : elles prennent l'air d'une relation ;
 *  - les ESPACEMENTS (`\quad` `\qquad` `\;` `\:`, avec `\ ` et `\,` déjà
 *    là), qui ne produisent JAMAIS de glyphe — seulement de la largeur ;
 *  - les RELATIONS et les LETTRES GRECQUES que le secondaire emploie
 *    vraiment : `\ne`, `\equiv`, et `\Delta` `\delta` `\alpha` `\beta`
 *    `\theta` (la variation d'un taux, les angles). L'alphabet grec entier
 *    n'entre pas : on n'ouvre pas une syntaxe qu'aucun contenu n'écrit.
 *
 * Le tilde `~` n'est pas une commande et n'est donc pas ici ; il est lu comme
 * une espace insécable, parce que Mathpix l'écrit systématiquement devant les
 * unités (`\mathrm{~cm}`) et qu'il ne peut rien vouloir dire d'autre.
 */
export const LATEX_COMMANDES_PERMISES = [
    // Fractions, racines
    '\\frac',
    '\\sqrt',
    // Ce que les fiches de cours écrivent (chantier « manuel vivant », AD-MV-15) :
    // les deux variantes de taille de la fraction, les points de suite, l'accolade
    // qui rassemble des facteurs, et le morceau nommé qu'une annotation vise.
    '\\dfrac',
    '\\tfrac',
    '\\cdots',
    '\\underbrace',
    '\\cible',
    // Opérations et relations
    '\\cdot',
    '\\times',
    '\\div',
    '\\pm',
    '\\neq',
    '\\leq',
    '\\geq',
    '\\le',
    '\\ge',
    '\\approx',
    '\\ne',
    '\\equiv',
    // Flèches — correspondance, implication, équivalence, image
    '\\to',
    '\\rightarrow',
    '\\Rightarrow',
    '\\Leftrightarrow',
    '\\mapsto',
    // Symboles
    '\\pi',
    '\\circ',
    '\\infty',
    '\\%',
    // Lettres grecques du secondaire : la variation, les angles
    '\\Delta',
    '\\delta',
    '\\alpha',
    '\\beta',
    '\\theta',
    // Géométrie
    '\\overline',
    '\\angle',
    '\\triangle',
    '\\vec',
    '\\parallel',
    '\\perp',
    // La norme d'un vecteur (tranche T43, P-NORME) : `\|\vec{u}\|`, ou
    // `\lVert \vec{u} \rVert`. Deux barres fines de chaque côté, jamais `||`.
    '\\|',
    '\\lVert',
    '\\rVert',
    // Ensembles (sec4-5)
    '\\in',
    '\\notin',
    '\\mathbb',
    '\\{',
    '\\}',
    '\\cup',
    '\\cap',
    // Le logarithme (tranche T22, F-85) : `\log x`, `\log_{2} 8`, `\ln x`
    '\\log',
    '\\ln',
    // La trigonométrie (tranche T39) : `\sin 30^{\circ}`, `\cos\theta`,
    // `\tan(x)`, `\sin^{-1}(0{,}5)`, `\sin^{2} x`
    '\\sin',
    '\\cos',
    '\\tan',
    // Les rapports réciproques (tranche T45, CAP-RECIPROQUES) : `\sec x`,
    // `\csc x` (affiché « cosec x »), `\cot x` (affiché « cotan x »).
    '\\sec',
    '\\csc',
    '\\cot',
    // Les matrices (tranche T41) : `\begin{pmatrix} 0 & -1 \\ 1 & 0 \end{pmatrix}`.
    // `\begin` et `\end` n'ouvrent QUE `pmatrix` (parenthèses) et `bmatrix`
    // (crochets) ; `&` et `\\` ne sont permis qu'À L'INTÉRIEUR de l'une d'elles.
    // Tout autre environnement (`cases`, `array`, `aligned`) reste refusé.
    '\\begin',
    '\\end',
    '\\\\',
    // Unités
    '\\text',
    '\\mathrm',
    // Délimiteurs étirables
    '\\left',
    '\\right',
    // Espaces — de la largeur, jamais un glyphe
    '\\ ',
    '\\,',
    '\\;',
    '\\:',
    '\\quad',
    '\\qquad',
];
const PERMISES = new Set(LATEX_COMMANDES_PERMISES);
/* ═════════════════════════════════════════════════════════ lexique */
/**
 * L'espace insécable : la seule qu'un rendu SVG ne mange pas en bord de
 * texte (une espace ordinaire en tête ou en queue de `<text>` est supprimée,
 * et le glyphe centré se décalerait d'autant).
 */
const ESPACE = '\u00A0';
/**
 * Barre suscrite COMBINANTE : `0,3̅`. Elle sert au seul repli en texte brut ;
 * le segment et le vecteur, eux, sont TRACÉS (nœud `surmonte`), car la flèche
 * combinante ne se raccorde pas d'un caractère à l'autre.
 */
const BARRE_SUSCRITE = '\u0305';
/**
 * `\cdots` : trois points médians. La police « données » les porte ; U+22EF,
 * le glyphe dédié, n'y est pas garanti. Exporté pour la description.
 */
export const POINTS_DE_SUITE = '···';
/** Opérateurs et relations écrits en commande. */
const OPERATEURS_COMMANDES = {
    cdot: '·',
    times: '×',
    div: '÷',
    pm: '±',
    neq: '≠',
    ne: '≠',
    equiv: '≡',
    to: '→',
    rightarrow: '→',
    Rightarrow: '⇒',
    Leftrightarrow: '⇔',
    mapsto: '↦',
    // Les points de suite d'un produit (`a \cdot a \cdots a`).
    cdots: POINTS_DE_SUITE,
    leq: '≤',
    le: '≤',
    geq: '≥',
    ge: '≥',
    approx: '≈',
    parallel: '∥',
    perp: '⊥',
    in: '∈',
    notin: '∉',
    cup: '∪',
    cap: '∩',
};
/**
 * Opérateurs, relations et séparateurs écrits en caractère. Les formes
 * Unicode (`×`, `≤`…) ne sont pas du LaTeX, mais un modèle en écrit : elles
 * disent la même chose que la commande, les refuser ne protégerait de rien.
 *
 * `,` `;` `:` sont des OPÉRATEURS et non des atomes : `decrireMath` intercale
 * « fois » entre deux opérandes voisins, et `[a, b]` se dirait « a fois
 * virgule fois b ». La virgule DÉCIMALE, elle, ne passe jamais par ici : elle
 * est lue avec son nombre (`3,14`).
 */
const OPERATEURS_CARACTERES = {
    '+': '+',
    '-': '−',
    '−': '−',
    '×': '×',
    '·': '·',
    '÷': '÷',
    '±': '±',
    '/': '/',
    '=': '=',
    '<': '<',
    '>': '>',
    '≤': '≤',
    '≥': '≥',
    '≠': '≠',
    '≈': '≈',
    '≡': '≡',
    /* La composition de deux fonctions, écrite en caractère (`f ∘ g`). */
    '∘': '∘',
    '→': '→',
    '⇒': '⇒',
    '⇔': '⇔',
    '↦': '↦',
    ':': ':',
    ',': ',',
    ';': ';',
};
/** Les signes qui, en position d'opérande, se lisent comme le signe d'un nombre. */
const SIGNES = new Set(['+', '−', '±']);
const SYMBOLES_COMMANDES = {
    pi: 'π',
    infty: '∞',
    Delta: 'Δ',
    delta: 'δ',
    alpha: 'α',
    beta: 'β',
    theta: 'θ',
};
/**
 * Les noms d'opérateur : écrits droits, tels quels (`\log`, `\ln`, et depuis
 * la tranche T39 `\sin`, `\cos`, `\tan`). Ils prennent un indice (la base
 * du logarithme) ou un exposant (`\sin^{-1}`, `\sin^{2} x`), puis une espace
 * fine devant ce qu'ils prennent, sauf devant une parenthèse.
 */
const OPERATEURS_NOMMES = new Set([
    'log',
    'ln',
    'sin',
    'cos',
    'tan',
    'sec',
    'csc',
    'cot',
]);
/**
 * Le nom ÉCRIT d'un opérateur, quand il diffère de sa commande (tranche T45) :
 * l'usage québécois écrit « cosec » et « cotan », pas `csc` et `cot`. La
 * commande reste celle de LaTeX ; seul ce que l'élève voit (et entend) change.
 */
const NOM_ECRIT = { csc: 'cosec', cot: 'cotan' };
/** Les noms tels qu'ils sont écrits dans l'arbre (`sin`, `cosec`, `cotan`…). */
const NOMS_ECRITS = new Set([...OPERATEURS_NOMMES].map((nom) => NOM_ECRIT[nom] ?? nom));
/** Les préfixes qui NOMMENT une figure : `\angle ABC` est un seul nom, pas un produit. */
const PREFIXES_GEOMETRIE = { angle: '∠', triangle: '△' };
/** Les ensembles de nombres du secondaire, et eux seuls. */
const ENSEMBLES = {
    N: 'ℕ',
    Z: 'ℤ',
    Q: 'ℚ',
    R: 'ℝ',
    C: 'ℂ',
};
/** Caractères qui se COLLENT à l'atome qui précède : `5!`, `f′`. */
const SUFFIXES_CARACTERES = {
    "'": '′',
    '!': '!',
    '.': '.',
    '°': '°',
};
const estChiffre = (c) => c >= '0' && c <= '9';
const estLettre = (c) => /^[A-Za-z]$/.test(c);
/** Les seuls environnements ouverts (tranche T41), et le délimiteur que chacun dessine. */
const ENVIRONNEMENTS_MATRICE = {
    pmatrix: 'parentheses',
    bmatrix: 'crochets',
};
/** Une matrice tient sur un écran de téléphone : au plus 6 lignes et 8 colonnes. */
const LIGNES_MATRICE_MAX = 6;
const COLONNES_MATRICE_MAX = 8;
/**
 * Découpe le contenu d'un segment en jetons, et relève d'un seul passage
 * tout ce qui interdit le dessin AVANT toute analyse : commandes hors liste,
 * `$`, `%` nu (un commentaire LaTeX, qui avalerait la suite), environnement,
 * accolades déséquilibrées. Tout est relevé, pas seulement le premier
 * défaut : le journal doit dire ce qu'il faudrait ajouter à la liste.
 */
function decouperLatex(contenu) {
    const jetons = [];
    const inconnues = [];
    const erreurs = [];
    const signaler = (message) => {
        if (!erreurs.includes(message))
            erreurs.push(message);
    };
    const car = [...contenu];
    let i = 0;
    /** Les environnements ouverts : `&` et `\\` n'ont de sens que dans une matrice. */
    const ouverts = [];
    while (i < car.length) {
        const c = car[i] ?? '';
        if (/\s/.test(c)) {
            while (i < car.length && /\s/.test(car[i] ?? ''))
                i += 1;
            jetons.push({ sorte: 'blanc' });
            continue;
        }
        if (c === '\\') {
            const suivant = car[i + 1];
            if (suivant === undefined) {
                signaler('barre oblique inverse en fin d’expression');
                i += 1;
                continue;
            }
            let nom;
            if (estLettre(suivant)) {
                let j = i + 1;
                while (j < car.length && estLettre(car[j] ?? ''))
                    j += 1;
                nom = car.slice(i + 1, j).join('');
                i = j;
            }
            else {
                // Un saut de ligne après `\` est l'espace de contrôle, comme `\ `.
                nom = /\s/.test(suivant) ? ' ' : suivant;
                i += 2;
            }
            const commande = `\\${nom}`;
            if (!PERMISES.has(commande) && !inconnues.includes(commande))
                inconnues.push(commande);
            if (nom === 'begin' || nom === 'end') {
                // Le nom de l'environnement est lu AVEC la commande : `\begin{pmatrix}`.
                let j = i;
                while (j < car.length && /\s/.test(car[j] ?? ''))
                    j += 1;
                let env = '';
                if (car[j] === '{') {
                    let k = j + 1;
                    while (k < car.length && estLettre(car[k] ?? ''))
                        k += 1;
                    if (car[k] === '*')
                        k += 1;
                    if (car[k] === '}') {
                        env = car.slice(j + 1, k).join('');
                        i = k + 1;
                    }
                }
                if (ENVIRONNEMENTS_MATRICE[env] === undefined) {
                    signaler(env === ''
                        ? `environnement \\${nom} sans nom`
                        : `environnement \\begin{${env}} interdit (seuls pmatrix et bmatrix)`);
                }
                else if (nom === 'begin') {
                    if (ouverts.length > 0)
                        signaler('matrice dans une matrice');
                    ouverts.push(env);
                }
                else if (ouverts.pop() !== env) {
                    signaler(`\\end{${env}} sans \\begin{${env}}`);
                }
                jetons.push({ sorte: 'environnement', ouvre: nom === 'begin', nom: env });
                continue;
            }
            if (nom === '\\' && ouverts.length === 0)
                signaler('« \\\\ » hors d’une matrice');
            jetons.push({ sorte: 'commande', nom });
            continue;
        }
        if (c === '&' && ouverts.length === 0)
            signaler('« & » hors d’une matrice');
        if (c === '$')
            signaler('le signe $ est interdit');
        if (c === '%')
            signaler('% non échappé (commentaire LaTeX), écrire \\%');
        jetons.push({ sorte: 'caractere', c });
        i += 1;
    }
    let profondeur = 0;
    let negative = false;
    for (const j of jetons) {
        if (j.sorte !== 'caractere')
            continue;
        if (j.c === '{')
            profondeur += 1;
        if (j.c === '}')
            profondeur -= 1;
        if (profondeur < 0)
            negative = true;
    }
    if (negative || profondeur !== 0)
        signaler('accolade déséquilibrée');
    if (ouverts.length > 0)
        signaler(`\\begin{${ouverts[0] ?? ''}} sans \\end`);
    return { jetons, inconnues, erreurs };
}
/* ═════════════════════════════════════════════════════════ lecture */
/** Un refus motivé. Levé au premier défaut, rattrapé au niveau du segment. */
class RefusLatex extends Error {
}
function refuser(message) {
    throw new RefusLatex(message);
}
const NON_FERME = {
    expression: 'expression inachevée',
    accolade: 'accolade non fermée',
    parenthese: 'parenthèse non fermée',
    crochet: 'crochet non fermé',
    ensemble: 'accolade \\{ sans \\}',
    absolu: 'valeur absolue non fermée',
    norme: 'norme non fermée',
    left: '\\left sans \\right',
    degre: 'indice de racine non fermé',
    cellule: 'matrice non fermée',
};
/** Garde-fou de pile : `disposerMath` refuserait de toute façon au-delà de 8 niveaux. */
const PROFONDEUR_MAX = 32;
/**
 * Un atome fait UNIQUEMENT d'espaces insécables — ce que produisent `\ `,
 * `\,`, `\;`, `\:`, `\quad` et `\qquad`. Ils sont transparents partout où
 * l'analyse cherche un opérande : une largeur n'est pas un terme.
 */
const estEspace = (n) => n !== undefined &&
    n.type === 'atome' &&
    n.texte.length > 0 &&
    [...n.texte].every((c) => c === ESPACE);
/**
 * Une espace de `n` cadratins d'espace insécable. Rien n'est « joli » ici : la
 * largeur d'un atome se calcule en points de code (`disposer.ts`), donc une
 * espace plus large est une espace de plus. `\quad` vaut une espace de mot en
 * LaTeX, `\qquad` le double.
 */
const espace = (n) => atome(ESPACE.repeat(n));
/**
 * `true` si le prochain élément occupe une place d'OPÉRANDE : en tête, ou
 * après un opérateur. C'est ce qui distingue `−5` (un nombre signé) de
 * `3 − 5`, et `|x|` qui s'ouvre de `|` qui se ferme. Les espaces explicites
 * sont transparentes.
 */
function attendOperande(enfants) {
    for (let i = enfants.length - 1; i >= 0; i -= 1) {
        const n = enfants[i];
        if (estEspace(n))
            continue;
        // Une cible ne change pas la nature de ce qu'elle marque.
        return n === undefined || sansCible(n).type === 'operateur';
    }
    return true;
}
/** Un nombre écrit par l'auteur : chiffres, séparateurs, période. Rien d'autre. */
const estNombre = (n) => n !== undefined && n.type === 'atome' && /^[0-9](?:[0-9,.\u00A0]|\u0305)*$/.test(n.texte);
const surligner = (texte) => [...texte].map((c) => `${c}${BARRE_SUSCRITE}`).join('');
class LecteurLatex {
    jetons;
    k = 0;
    profondeur = 0;
    /**
     * Les signes lus en place d'opérande. `NoeudMath` n'a pas de nœud « signe » ;
     * l'identité de l'objet suffit à les retrouver au moment de clore la rangée.
     */
    unaires = new Set();
    constructor(jetons) {
        this.jetons = jetons;
    }
    lireTout() {
        return this.lireSuite('expression').noeud;
    }
    get courant() {
        return this.jetons[this.k];
    }
    sauterBlancs() {
        while (this.courant?.sorte === 'blanc')
            this.k += 1;
    }
    estCaractere(c, decalage = 0) {
        const j = this.jetons[this.k + decalage];
        return j !== undefined && j.sorte === 'caractere' && j.c === c;
    }
    estCommande(nom) {
        const j = this.courant;
        return j !== undefined && j.sorte === 'commande' && j.nom === nom;
    }
    estChiffreCourant(decalage = 0) {
        const j = this.jetons[this.k + decalage];
        return j !== undefined && j.sorte === 'caractere' && estChiffre(j.c);
    }
    /* ─────────────────────────────────────────────────────── les suites */
    /**
     * Lit une suite jusqu'à ce qui ferme `cadre`, et rend aussi CE qui l'a
     * fermée : c'est ce qui permet de distinguer `[a, b]` de `[a, b[`.
     */
    lireSuite(cadre) {
        this.profondeur += 1;
        if (this.profondeur > PROFONDEUR_MAX)
            refuser('expression trop imbriquée');
        const enfants = [];
        // Vrai tant que le dernier élément est un script que CETTE rangée vient
        // de former : `x^2_1` complète le même nœud ; `{x^2}^3` en crée un autre.
        let scriptOuvert = false;
        for (;;) {
            this.sauterBlancs();
            const j = this.courant;
            if (j === undefined) {
                if (cadre !== 'expression')
                    refuser(NON_FERME[cadre]);
                break;
            }
            const operande = attendOperande(enfants);
            const fermant = this.fermantDe(cadre, j, operande);
            if (fermant !== null) {
                this.k += 1;
                this.profondeur -= 1;
                return { noeud: this.clore(enfants, cadre), fermant };
            }
            if (j.sorte === 'caractere' && (j.c === '^' || j.c === '_')) {
                this.k += 1;
                this.attacherScript(enfants, j.c, operande, scriptOuvert);
                scriptOuvert = true;
                continue;
            }
            scriptOuvert = false;
            this.lireElement(enfants, operande);
        }
        this.profondeur -= 1;
        return { noeud: this.clore(enfants, cadre), fermant: '' };
    }
    fermantDe(cadre, j, operande) {
        if (j.sorte === 'blanc')
            return null;
        if (j.sorte === 'environnement')
            return cadre === 'cellule' && !j.ouvre ? 'end' : null;
        if (j.sorte === 'commande') {
            if (cadre === 'cellule' && j.nom === '\\')
                return '\\';
            if (cadre === 'ensemble' && j.nom === '}')
                return '}';
            if (cadre === 'left' && j.nom === 'right')
                return 'right';
            if (cadre === 'norme' && (j.nom === 'rVert' || (j.nom === '|' && !operande)))
                return j.nom;
            return null;
        }
        const c = j.c;
        switch (cadre) {
            case 'accolade':
                return c === '}' ? c : null;
            case 'parenthese':
                // `(a, b]` : notation anglaise d'un intervalle semi-ouvert.
                return c === ')' || c === ']' ? c : null;
            case 'crochet':
                // Un `[` en place d'opérateur FERME : `[a, b[` est un intervalle
                // ouvert à droite, la notation de l'école québécoise.
                return c === ']' || c === ')' || (c === '[' && !operande) ? c : null;
            case 'absolu':
                return c === '|' && !operande ? c : null;
            case 'norme':
                return c === '‖' && !operande ? c : null;
            case 'degre':
                return c === ']' ? c : null;
            case 'cellule':
                return c === '&' ? c : null;
            default:
                return null;
        }
    }
    /**
     * Clôt une rangée. Un signe lu en place d'opérande et suivi d'un atome
     * s'y colle : `−5` est UN nombre — dessiné sans l'air d'une soustraction,
     * dit « −5 » et non « − fois 5 ». Devant une structure (`−\sqrt{9}`), il
     * reste un opérateur. La fusion se fait ici, APRÈS les scripts, pour que
     * `-x^2` reste −(x²) et ne devienne jamais (−x)².
     */
    clore(enfants, cadre) {
        const fusion = [];
        for (let i = 0; i < enfants.length; i += 1) {
            const n = enfants[i];
            if (n === undefined)
                continue;
            const suivant = enfants[i + 1];
            if (n.type === 'operateur' &&
                this.unaires.has(n) &&
                suivant?.type === 'atome' &&
                suivant.texte !== '' &&
                !estEspace(suivant)) {
                fusion.push(atome(`${n.texte}${suivant.texte}`));
                i += 1;
                continue;
            }
            fusion.push(n);
        }
        if (fusion.length > 0)
            return rangee(fusion);
        // Un groupe VISIBLE vide (`\{ \}`, l'ensemble vide) garde la hauteur d'une
        // ligne, sinon ses délimiteurs s'écraseraient ; des accolades de
        // regroupement vides (`{}^{3}`) ne dessinent rien.
        return cadre === 'accolade' ||
            cadre === 'expression' ||
            cadre === 'degre' ||
            cadre === 'cellule'
            ? atome('')
            : atome(ESPACE);
    }
    /* ──────────────────────────────────────────────────────── les scripts */
    /**
     * `^` et `_` s'attachent au DERNIER élément, comme en LaTeX : dans `2x^2`,
     * seul `x` est au carré. Sans base (`{}_{1}`, `^{3}` en tête), la base est
     * vide — LaTeX le dessine ainsi, Mathpix l'écrit pour un indice isolé.
     */
    attacherScript(enfants, sorte, operande, scriptOuvert) {
        const base = operande ? atome('') : (enfants.pop() ?? atome(''));
        // `90^{\circ}` : le degré est un CARACTÈRE, pas un exposant. Écrit `°`,
        // il reste du texte, se coupe et se copie ; dessiné en exposant, il
        // obligerait un SVG pour rien.
        if (sorte === '^' && this.lireDegre()) {
            if (base.type === 'atome') {
                enfants.push(atome(`${base.texte}°`));
                return;
            }
            enfants.push(this.completerScript(base, 'exposant', atome('°'), scriptOuvert));
            return;
        }
        const argument = this.lireArgument(sorte === '^' ? 'l’exposant' : 'l’indice');
        enfants.push(this.completerScript(base, sorte === '^' ? 'exposant' : 'indice', argument, scriptOuvert));
        /* `\log_{2} 8` : l'espace fine du logarithme se pose APRÈS sa base. */
        if (base.type === 'atome' && NOMS_ECRITS.has(base.texte))
            this.espacerLOperande(enfants);
    }
    /** Pose une espace fine après un nom d'opérateur, sauf devant un script ou une parenthèse. */
    espacerLOperande(enfants) {
        const retour = this.k;
        this.sauterBlancs();
        const suivant = this.courant;
        this.k = retour;
        if (suivant === undefined)
            return;
        if (suivant.sorte === 'caractere' && ['_', '^', '(', ')', '}'].includes(suivant.c))
            return;
        if (suivant.sorte === 'commande' && (suivant.nom === 'left' || suivant.nom === 'right'))
            return;
        enfants.push(espace(1));
    }
    completerScript(base, place, argument, scriptOuvert) {
        if (scriptOuvert && base.type === 'script') {
            if (base[place] !== undefined) {
                refuser(place === 'exposant' ? 'double exposant' : 'double indice');
            }
            return { ...base, [place]: argument };
        }
        return place === 'exposant'
            ? { type: 'script', base, exposant: argument }
            : { type: 'script', base, indice: argument };
    }
    /** Consomme `\circ` ou `{\circ}` s'il est là ; sinon ne touche à rien. */
    lireDegre() {
        const retour = this.k;
        this.sauterBlancs();
        if (this.estCommande('circ')) {
            this.k += 1;
            return true;
        }
        if (this.estCaractere('{')) {
            this.k += 1;
            this.sauterBlancs();
            if (this.estCommande('circ')) {
                this.k += 1;
                this.sauterBlancs();
                if (this.estCaractere('}')) {
                    this.k += 1;
                    return true;
                }
            }
        }
        this.k = retour;
        return false;
    }
    /**
     * L'argument d'une commande ou d'un script : un groupe `{…}`, ou UN seul
     * jeton — `x^23` est x² suivi de 3, `\frac12` est un demi. C'est la règle de
     * LaTeX, et la suivre est la seule façon de dessiner ce qui est écrit.
     */
    lireArgument(de) {
        this.sauterBlancs();
        const j = this.courant;
        if (j === undefined)
            refuser(`${de} manque`);
        if (j.sorte === 'caractere' && j.c === '{') {
            this.k += 1;
            return this.lireSuite('accolade').noeud;
        }
        this.k += 1;
        if (j.sorte === 'caractere') {
            if (estChiffre(j.c) || estLettre(j.c) || j.c === '?')
                return atome(j.c);
            const op = OPERATEURS_CARACTERES[j.c];
            if (op !== undefined && SIGNES.has(op))
                return atome(op);
        }
        if (j.sorte === 'commande') {
            const symbole = SYMBOLES_COMMANDES[j.nom];
            if (symbole !== undefined)
                return atome(symbole);
        }
        return refuser(`${de} doit être entre accolades`);
    }
    /* ─────────────────────────────────────────────────────── les éléments */
    lireElement(enfants, operande) {
        const j = this.courant;
        if (j === undefined || j.sorte === 'blanc')
            return;
        if (j.sorte === 'environnement') {
            this.k += 1;
            if (!j.ouvre)
                refuser(`\\end{${j.nom}} sans \\begin{${j.nom}}`);
            enfants.push(this.lireMatrice(j.nom));
            return;
        }
        if (j.sorte === 'commande') {
            this.k += 1;
            this.lireCommande(enfants, j.nom, operande);
            return;
        }
        const c = j.c;
        if (estChiffre(c)) {
            enfants.push(atome(this.lireNombre()));
            return;
        }
        this.k += 1;
        if (estLettre(c) || c === '?' || c === 'π' || c === '∞') {
            enfants.push(atome(c));
            return;
        }
        if (c === '|') {
            // En place d'opérande, `|` ouvre une valeur absolue ; ailleurs c'est le
            // « tel que » d'un ensemble (`\{x \in \mathbb{R} | x > 2\}`).
            if (operande) {
                enfants.push({ type: 'valeurAbsolue', contenu: this.lireSuite('absolu').noeud });
            }
            else {
                enfants.push(operateur('|'));
            }
            return;
        }
        if (c === '‖') {
            // Le caractère « ‖ » d'un modèle dit la même chose que `\|` (tranche T43).
            this.lireNorme(enfants, operande, '‖');
            return;
        }
        if (c === '(') {
            const { noeud, fermant } = this.lireSuite('parenthese');
            enfants.push(delimiter('(', noeud, fermant));
            return;
        }
        if (c === '[' || (c === ']' && operande)) {
            // `]` en place d'opérande OUVRE : `]a, b[`, l'intervalle ouvert à gauche.
            const { noeud, fermant } = this.lireSuite('crochet');
            enfants.push(delimiter(c, noeud, fermant));
            return;
        }
        if (c === '{') {
            // Les accolades groupent SANS se voir : c'est leur sens en LaTeX.
            enfants.push(this.lireSuite('accolade').noeud);
            return;
        }
        if (c === ')' || c === ']' || c === '}')
            refuser(`« ${c} » fermant sans ouvrant`);
        if (c === '~') {
            enfants.push(atome(ESPACE));
            return;
        }
        const suffixe = SUFFIXES_CARACTERES[c];
        if (suffixe !== undefined) {
            coller(enfants, suffixe);
            return;
        }
        const op = OPERATEURS_CARACTERES[c];
        if (op !== undefined) {
            this.pousserOperateur(enfants, op, operande);
            return;
        }
        refuser(`caractère non pris en charge : « ${c} »`);
    }
    /**
     * Une MATRICE (tranche T41) : des cases séparées par `&`, des lignes par
     * `\\`, jusqu'au `\end` du même nom. Toutes les lignes ont le même nombre de
     * cases — une matrice bancale n'est pas une matrice, elle est refusée. Un `\\`
     * final, que LaTeX tolère, n'ajoute pas de ligne vide.
     */
    lireMatrice(env) {
        const delimiteur = ENVIRONNEMENTS_MATRICE[env];
        if (delimiteur === undefined)
            return refuser(`environnement \\begin{${env}} interdit`);
        const lignes = [[]];
        for (;;) {
            const { noeud, fermant } = this.lireSuite('cellule');
            lignes[lignes.length - 1]?.push(noeud);
            if (fermant === '&')
                continue;
            if (fermant === '\\') {
                lignes.push([]);
                continue;
            }
            break;
        }
        const derniere = lignes[lignes.length - 1];
        const estVide = (n) => n?.type === 'atome' && n.texte === '';
        if (lignes.length > 1 && derniere?.length === 1 && estVide(derniere[0]))
            lignes.pop();
        const colonnes = lignes[0]?.length ?? 0;
        if (lignes.some((l) => l.length !== colonnes)) {
            refuser('matrice aux lignes de longueurs inégales');
        }
        if (lignes.some((l) => l.some(estVide)))
            refuser('case de matrice vide');
        if (lignes.length > LIGNES_MATRICE_MAX || colonnes > COLONNES_MATRICE_MAX) {
            refuser(`matrice trop grande (au plus ${LIGNES_MATRICE_MAX} × ${COLONNES_MATRICE_MAX})`);
        }
        return { type: 'matrice', lignes, delimiteur };
    }
    /**
     * La NORME (tranche T43) : `\|\vec{u}\|`, `‖\vec{u}‖` ou `\lVert \vec{u} \rVert`.
     * Comme `|`, `\|` n'OUVRE qu'en place d'opérande ; ailleurs il n'a pas de
     * sens dans le programme (le parallélisme s'écrit `\parallel`), et il est
     * refusé plutôt que deviné.
     */
    lireNorme(enfants, operande, ecrit) {
        if (!operande && ecrit !== '\\lVert') {
            refuser(`« ${ecrit} » hors d’une norme (le parallélisme s’écrit \\parallel)`);
        }
        enfants.push({ type: 'norme', contenu: this.lireSuite('norme').noeud });
    }
    pousserOperateur(enfants, texte, operande) {
        const noeud = operateur(texte);
        if (operande && SIGNES.has(texte))
            this.unaires.add(noeud);
        enfants.push(noeud);
    }
    lireCommande(enfants, nom, operande) {
        const op = OPERATEURS_COMMANDES[nom];
        if (op !== undefined) {
            this.pousserOperateur(enfants, op, operande);
            return;
        }
        const symbole = SYMBOLES_COMMANDES[nom];
        if (symbole !== undefined) {
            enfants.push(atome(symbole));
            return;
        }
        const prefixe = PREFIXES_GEOMETRIE[nom];
        if (prefixe !== undefined) {
            enfants.push(atome(`${prefixe}${this.lireNomGeometrique()}`));
            return;
        }
        /* LE LOGARITHME : un NOM d'opérateur, droit, suivi d'une espace fine devant
           ce qu'il prend — « log 8 », « ln x » — sauf devant sa base (`\log_{2}`) ou
           une parenthèse. Écrit `\mathrm{log}`, c'était une unité : après un nombre,
           « 2 log » se collait en une seule grandeur. */
        if (OPERATEURS_NOMMES.has(nom)) {
            /* Après un facteur (`2\sin x`, `a\log b`), la même espace fine DEVANT
               le nom : « 2 sin x », jamais « 2sin x » (tranche T39). */
            const avant = enfants[enfants.length - 1];
            if (avant !== undefined &&
                avant.type !== 'operateur' &&
                !estEspace(avant) &&
                !(avant.type === 'atome' && avant.texte === '')) {
                enfants.push(espace(1));
            }
            enfants.push(atome(NOM_ECRIT[nom] ?? nom));
            this.espacerLOperande(enfants);
            return;
        }
        switch (nom) {
            /* `\dfrac` et `\tfrac` ne disent que la TAILLE. Le moteur la décide déjà
               seul : une fraction en exposant ou dans une fraction descend d'un cran,
               une fraction sur la ligne garde le corps. Les trois se lisent donc de
               la même façon — ce qui n'est juste que parce qu'aucun contenu n'écrit
               `\tfrac` sur la ligne ni `\dfrac` en exposant. */
            case 'dfrac':
            case 'tfrac':
            case 'frac': {
                const numerateur = this.lireArgument('le numérateur');
                const denominateur = this.lireArgument('le dénominateur');
                enfants.push({ type: 'fraction', numerateur, denominateur });
                return;
            }
            case 'sqrt': {
                this.sauterBlancs();
                let degre;
                if (this.estCaractere('[')) {
                    this.k += 1;
                    const lu = this.lireSuite('degre').noeud;
                    if (!(lu.type === 'atome' && lu.texte === ''))
                        degre = lu;
                }
                const radicande = this.lireArgument('le radicande');
                enfants.push({ type: 'racine', radicande, ...(degre === undefined ? {} : { degre }) });
                return;
            }
            case 'underbrace': {
                const contenu = this.lireArgument('le contenu de l’accolade');
                this.sauterBlancs();
                if (!this.estCaractere('_'))
                    refuser('\\underbrace attend sa légende : _{…}');
                this.k += 1;
                const legende = this.lireArgument('la légende de l’accolade');
                enfants.push({ type: 'sousAccolade', contenu, legende });
                return;
            }
            case 'cible': {
                const nomCible = this.lireTexteSimple('\\cible');
                const contenu = this.lireArgument('le contenu de la cible');
                enfants.push({ type: 'cible', nom: nomCible, contenu });
                return;
            }
            case 'overline':
                enfants.push({
                    type: 'surmonte',
                    contenu: atome(this.lireTexteSimple('\\overline')),
                    ornement: 'barre',
                });
                return;
            case 'vec': {
                /* La flèche est TRACÉE à la largeur du contenu (nœud « surmonte ») :
                   `\vec{AB}` — la notation du manuel québécois — se compose donc
                   enfin, là où un caractère combinant donnait deux vecteurs A et B. */
                enfants.push({
                    type: 'surmonte',
                    contenu: atome(this.lireTexteSimple('\\vec')),
                    ornement: 'fleche',
                });
                return;
            }
            case 'mathbb': {
                const lettre = this.lireTexteSimple('\\mathbb');
                const ensemble = ENSEMBLES[lettre];
                if (ensemble === undefined)
                    refuser(`\\mathbb{${lettre}} n’est pas un ensemble du secondaire`);
                enfants.push(atome(ensemble));
                return;
            }
            case 'text':
            case 'mathrm':
                pousserTexte(enfants, this.lireTexte(nom));
                return;
            case 'left': {
                const ouvrant = this.lireDelimiteur('left');
                const { noeud } = this.lireSuite('left');
                const fermant = this.lireDelimiteur('right');
                enfants.push(delimiter(ouvrant, noeud, fermant));
                return;
            }
            case '{':
                enfants.push({
                    type: 'groupe',
                    contenu: this.lireSuite('ensemble').noeud,
                    delimiteur: 'accolades',
                });
                return;
            case '%':
                coller(enfants, '%');
                return;
            case ' ':
            case ',':
            case ';':
            case ':':
                enfants.push(espace(1));
                return;
            case 'quad':
                enfants.push(espace(2));
                return;
            case 'qquad':
                enfants.push(espace(4));
                return;
            case 'circ':
                /* Hors de l'exposant — où `attacherScript` l'a déjà lue comme le degré —,
                   `\circ` est la COMPOSITION de deux fonctions : un opérateur binaire,
                   posé comme `\times`. `f \circ g`, `(f \circ g)(x)`. */
                this.pousserOperateur(enfants, '∘', operande);
                return;
                return;
            case 'right':
                refuser('\\right sans \\left');
                return;
            case '|':
                this.lireNorme(enfants, operande, '\\|');
                return;
            case 'lVert':
                this.lireNorme(enfants, operande, '\\lVert');
                return;
            case 'rVert':
                refuser('\\rVert sans \\lVert');
                return;
            case '\\':
                refuser('« \\\\ » hors d’une case de matrice');
                return;
            case '}':
                refuser('\\} sans \\{');
                return;
            default:
                // Inatteignable : le lexique a déjà refusé toute commande hors liste.
                refuser(`commande non prise en charge : \\${nom}`);
        }
    }
    /* ───────────────────────────────────────────────────────── les nombres */
    lireChiffres() {
        let texte = '';
        while (this.estChiffreCourant()) {
            const j = this.courant;
            if (j?.sorte === 'caractere')
                texte += j.c;
            this.k += 1;
        }
        return texte;
    }
    /**
     * Un nombre, tel que l'école québécoise l'écrit : `3,14` (virgule
     * décimale COLLÉE aux chiffres — espacée, c'est un séparateur de liste),
     * `3{,}14`, `1 000` et `1\,000` (tranches de trois), `0,\overline{3}` et le
     * `7 , \overline { 9 }` de Mathpix (développement périodique).
     */
    lireNombre() {
        let texte = this.lireChiffres();
        // Tranches de milliers : une espace, puis EXACTEMENT trois chiffres.
        for (;;) {
            const retour = this.k;
            if (!this.sauterSeparateurMilliers())
                break;
            const tranche = this.lireChiffres();
            if (tranche.length !== 3) {
                this.k = retour;
                break;
            }
            texte += `${ESPACE}${tranche}`;
        }
        let decimale = false;
        const sep = this.courant;
        if (sep?.sorte === 'caractere' &&
            (sep.c === ',' || sep.c === '.') &&
            this.estChiffreCourant(1)) {
            this.k += 1;
            texte += `${sep.c}${this.lireChiffres()}`;
            decimale = true;
        }
        else if (this.estCaractere('{') &&
            this.estCaractere(',', 1) &&
            this.estCaractere('}', 2) &&
            this.estChiffreCourant(3)) {
            this.k += 3;
            texte += `,${this.lireChiffres()}`;
            decimale = true;
        }
        // La période : `\overline` sur des chiffres, après la partie décimale ou
        // juste après la virgule.
        const avant = this.k;
        this.sauterBlancs();
        let virgule = '';
        if (!decimale && (this.estCaractere(',') || this.estCaractere('.'))) {
            const j = this.courant;
            virgule = j?.sorte === 'caractere' ? j.c : '';
            this.k += 1;
            this.sauterBlancs();
        }
        const periode = this.lirePeriode();
        if (periode !== null && (decimale || virgule !== '')) {
            texte += `${virgule}${surligner(periode)}`;
        }
        else {
            this.k = avant;
        }
        return texte;
    }
    sauterSeparateurMilliers() {
        let vu = false;
        let explicite = false;
        for (;;) {
            const j = this.courant;
            if (j?.sorte === 'blanc') {
                this.k += 1;
                vu = true;
                continue;
            }
            const espace = (j?.sorte === 'commande' && (j.nom === ' ' || j.nom === ',')) ||
                (j?.sorte === 'caractere' && j.c === '~');
            if (espace && !explicite) {
                this.k += 1;
                vu = true;
                explicite = true;
                continue;
            }
            return vu;
        }
    }
    /** `\overline{chiffres}` → les chiffres ; tout autre chose → `null`, sans rien consommer. */
    lirePeriode() {
        if (!this.estCommande('overline'))
            return null;
        const retour = this.k;
        this.k += 1;
        this.sauterBlancs();
        if (this.estChiffreCourant())
            return this.lireChiffres().slice(0, 1) || null;
        if (this.estCaractere('{')) {
            this.k += 1;
            let chiffres = '';
            for (;;) {
                this.sauterBlancs();
                if (!this.estChiffreCourant())
                    break;
                chiffres += this.lireChiffres();
            }
            if (this.estCaractere('}') && chiffres !== '') {
                this.k += 1;
                return chiffres;
            }
        }
        this.k = retour;
        return null;
    }
    /* ─────────────────────────────────────────────────────── les textes */
    /** L'argument de `\overline`, `\vec`, `\mathbb` : des lettres et des chiffres, rien d'autre. */
    lireTexteSimple(commande) {
        this.sauterBlancs();
        const j = this.courant;
        if (j?.sorte === 'caractere' && (estLettre(j.c) || estChiffre(j.c))) {
            this.k += 1;
            return j.c;
        }
        if (!this.estCaractere('{'))
            refuser(`${commande} attend des accolades`);
        this.k += 1;
        let texte = '';
        for (;;) {
            this.sauterBlancs();
            const x = this.courant;
            if (x?.sorte === 'caractere' && (estLettre(x.c) || estChiffre(x.c))) {
                texte += x.c;
                this.k += 1;
                continue;
            }
            break;
        }
        if (!this.estCaractere('}') || texte === '') {
            refuser(`${commande}{…} n’accepte que des lettres et des chiffres`);
        }
        this.k += 1;
        return texte;
    }
    /**
     * Le nom qui suit `\angle` ou `\triangle` : `ABC`, ou le `A B C` espacé de
     * Mathpix, primes compris (`A′B′C′`).
     */
    lireNomGeometrique() {
        let nom = '';
        for (;;) {
            const retour = this.k;
            this.sauterBlancs();
            const j = this.courant;
            if (j?.sorte === 'caractere' && estLettre(j.c)) {
                nom += j.c;
                this.k += 1;
                continue;
            }
            if (j?.sorte === 'caractere' && j.c === "'" && nom !== '') {
                nom += '′';
                this.k += 1;
                continue;
            }
            this.k = retour;
            return nom;
        }
    }
    /**
     * Le contenu de `\text{…}` ou `\mathrm{…}`, pris LITTÉRALEMENT : accents,
     * `/` et chiffres compris (`\text{km/h}`). Chaque suite de blancs devient
     * une espace insécable, qui survit au rendu.
     */
    lireTexte(nom) {
        this.sauterBlancs();
        if (!this.estCaractere('{'))
            refuser(`\\${nom} attend des accolades`);
        this.k += 1;
        let texte = '';
        for (;;) {
            const j = this.courant;
            if (j === undefined)
                refuser(`\\${nom}{…} non fermé`);
            this.k += 1;
            if (j.sorte === 'blanc') {
                texte += ' ';
                continue;
            }
            if (j.sorte === 'commande') {
                if (j.nom === '%' || j.nom === '{' || j.nom === '}')
                    texte += j.nom;
                else if (j.nom === ' ' || j.nom === ',')
                    texte += ' ';
                else
                    refuser(`\\${j.nom} dans \\${nom}`);
                continue;
            }
            if (j.sorte === 'environnement')
                refuser(`matrice dans \\${nom}`);
            if (j.c === '}')
                break;
            if (j.c === '{')
                refuser(`accolade imbriquée dans \\${nom}`);
            if (j.c === '^' || j.c === '_')
                refuser(`« ${j.c} » dans \\${nom}`);
            texte += j.c === '~' ? ' ' : j.c;
        }
        return texte.replace(/\s+/g, ESPACE);
    }
    lireDelimiteur(cote) {
        this.sauterBlancs();
        const j = this.courant;
        this.k += 1;
        if (j?.sorte === 'commande' && (j.nom === '{' || j.nom === '}'))
            return j.nom;
        // `\left\| … \right\|` (et `\lVert`, `\rVert`) : la norme (tranche T43).
        if (j?.sorte === 'commande' && ['|', 'lVert', 'rVert'].includes(j.nom))
            return '‖';
        if (j?.sorte === 'caractere' && '()[]|‖'.includes(j.c))
            return j.c;
        return refuser(`délimiteur non pris en charge après \\${cote}`);
    }
}
/**
 * Le nœud d'une paire de délimiteurs. Les paires assorties s'ÉTIRENT
 * (`groupe`, `valeurAbsolue`) ; les intervalles `]a, b[`, `[a, b[`, `(a, b]`
 * n'ont pas de nœud à délimiteurs dépareillés, ils sont posés en opérateurs
 * — justes, lisibles, mais pas étirés. Toute autre paire est refusée.
 */
function delimiter(ouvrant, contenu, fermant) {
    if (ouvrant === '(' && fermant === ')')
        return { type: 'groupe', contenu, delimiteur: 'parentheses' };
    if (ouvrant === '[' && fermant === ']')
        return { type: 'groupe', contenu, delimiteur: 'crochets' };
    if (ouvrant === '{' && fermant === '}')
        return { type: 'groupe', contenu, delimiteur: 'accolades' };
    if (ouvrant === '|' && fermant === '|')
        return { type: 'valeurAbsolue', contenu };
    if (ouvrant === '‖' && fermant === '‖')
        return { type: 'norme', contenu };
    if ('([]'.includes(ouvrant) && ')[]'.includes(fermant)) {
        return rangee([operateur(ouvrant), contenu, operateur(fermant)]);
    }
    return refuser(`délimiteurs mal appariés : ${ouvrant} … ${fermant}`);
}
/**
 * Colle un suffixe (`%`, `!`, `′`, `°`) à l'atome qui précède : `25 %` est
 * UN opérande, et `decrireMath` le dirait sinon « 25 fois % ».
 */
function coller(enfants, suffixe) {
    const dernier = enfants[enfants.length - 1];
    if (dernier?.type === 'atome' && dernier.texte !== '' && !estEspace(dernier)) {
        enfants[enfants.length - 1] = atome(`${dernier.texte}${suffixe}`);
        return;
    }
    enfants.push(atome(suffixe));
}
/**
 * Pose un texte (`\text`, `\mathrm`). Juste après un nombre, c'est une
 * UNITÉ, et elle s'y colle en une seule grandeur : `25 cm`, séparé par
 * l'espace insécable que la typographie québécoise demande — que l'auteur
 * l'ait écrite ou non. Un exposant qui suit (`\text{cm}^{3}`) se pose alors
 * à droite de la grandeur, exactement où LaTeX le poserait, et le lecteur
 * d'écran dit « 25 cm au cube » plutôt que « 25 fois cm au cube ».
 */
function pousserTexte(enfants, texte) {
    if (texte === '')
        return;
    let i = enfants.length - 1;
    while (i >= 0 && estEspace(enfants[i]))
        i -= 1;
    const precedent = enfants[i];
    if (estNombre(precedent)) {
        enfants.splice(i);
        const separe = texte.startsWith(ESPACE) ? texte : `${ESPACE}${texte}`;
        enfants.push(atome(`${precedent.texte}${separe}`));
        return;
    }
    enfants.push(atome(texte));
}
const echec = (erreurs, commandesInconnues = []) => ({
    dessinable: false,
    commandesInconnues,
    erreurs,
});
/**
 * Analyse le CONTENU d'un segment (sans ses délimiteurs). Tout ou rien : un
 * seul défaut, et rien n'est dessiné — jamais une moitié d'expression à côté
 * d'une moitié de LaTeX brut.
 */
function analyserContenu(contenu) {
    const lexique = decouperLatex(contenu);
    if (lexique.inconnues.length > 0 || lexique.erreurs.length > 0) {
        const erreurs = lexique.inconnues.length > 0
            ? [`commande hors liste : ${lexique.inconnues.join(' ')}`, ...lexique.erreurs]
            : lexique.erreurs;
        return echec(erreurs, lexique.inconnues);
    }
    if (lexique.jetons.every((j) => j.sorte === 'blanc'))
        return echec(['expression vide']);
    let noeud;
    try {
        noeud = new LecteurLatex(lexique.jetons).lireTout();
    }
    catch (e) {
        if (e instanceof RefusLatex)
            return echec([e.message]);
        throw e;
    }
    // La même borne que le rendu : ce que `disposerMath` refuserait retombe
    // ICI, pour que « dessiné » veuille dire dessiné, dans l'app comme au journal.
    if (disposerMath(noeud) === null)
        return echec(['trop profonde ou trop lourde pour la mise en page']);
    return { dessinable: true, noeud };
}
/**
 * Sépare la prose des segments `\( … \)`. Une barre oblique inverse consomme
 * toujours le caractère qui la suit, si bien que `\\(` (saut de ligne LaTeX
 * suivi d'une parenthèse) n'ouvre pas de segment. `\[ … \]` n'est PAS un
 * segment : le document 22 l'interdit ; il reste de la prose et le
 * diagnostic le signale.
 */
function decouperPortions(texte) {
    const portions = [];
    const anomalies = [];
    let debutProse = 0;
    let i = 0;
    const fermerProse = (fin) => {
        if (fin > debutProse)
            portions.push({ sorte: 'prose', texte: texte.slice(debutProse, fin) });
    };
    while (i < texte.length) {
        if (texte[i] !== '\\') {
            i += 1;
            continue;
        }
        const suivant = texte[i + 1];
        if (suivant === '(') {
            fermerProse(i);
            const fin = chercherFermeture(texte, i + 2);
            if (fin < 0) {
                portions.push({ sorte: 'segment', source: texte.slice(i), contenu: null });
                i = texte.length;
            }
            else {
                portions.push({
                    sorte: 'segment',
                    source: texte.slice(i, fin + 2),
                    contenu: texte.slice(i + 2, fin),
                });
                i = fin + 2;
            }
            debutProse = i;
            continue;
        }
        if (suivant === '[')
            anomalies.push('\\[ … \\] interdit, écrire \\( … \\)');
        if (suivant === ')')
            anomalies.push('\\) sans \\( ouvrant');
        i += 2;
    }
    fermerProse(texte.length);
    return { portions, anomalies };
}
function chercherFermeture(texte, depart) {
    let j = depart;
    while (j < texte.length) {
        if (texte[j] === '\\') {
            if (texte[j + 1] === ')')
                return j;
            j += 2;
            continue;
        }
        j += 1;
    }
    return -1;
}
/**
 * Découpe `texte` en prose et en expressions à dessiner. Hors de `\( … \)` :
 * de la prose, telle quelle. Dans `\( … \)` : une expression, ou — au
 * moindre défaut — le segment brut, rendu à la prose qui l'entoure.
 */
export function analyserTexteLatex(texte) {
    if (texte.length === 0)
        return [];
    const segments = [];
    const pousserProse = (morceau) => {
        const dernier = segments[segments.length - 1];
        if (dernier?.type === 'prose') {
            segments[segments.length - 1] = { type: 'prose', texte: `${dernier.texte}${morceau}` };
        }
        else {
            segments.push({ type: 'prose', texte: morceau });
        }
    };
    for (const portion of decouperPortions(texte).portions) {
        if (portion.sorte === 'prose') {
            pousserProse(portion.texte);
            continue;
        }
        const analyse = portion.contenu === null ? null : analyserContenu(portion.contenu);
        if (analyse !== null && analyse.dessinable) {
            segments.push({ type: 'expression', source: portion.source, noeud: analyse.noeud });
        }
        else {
            pousserProse(portion.source);
        }
    }
    return segments;
}
const EXTRAIT_MAX = 60;
function extrait(source) {
    const plat = source.replace(/\s+/g, ' ');
    return plat.length <= EXTRAIT_MAX ? plat : `${plat.slice(0, EXTRAIT_MAX - 1)}…`;
}
export function diagnostiquerLatex(texte) {
    const { portions, anomalies } = decouperPortions(texte);
    let segments = 0;
    let dessines = 0;
    const commandesInconnues = [];
    const erreurs = [];
    for (const portion of portions) {
        if (portion.sorte !== 'segment')
            continue;
        segments += 1;
        if (portion.contenu === null) {
            erreurs.push(`\\( sans \\) fermant — ${extrait(portion.source)}`);
            continue;
        }
        const analyse = analyserContenu(portion.contenu);
        if (analyse.dessinable) {
            dessines += 1;
            continue;
        }
        for (const commande of analyse.commandesInconnues) {
            if (!commandesInconnues.includes(commande))
                commandesInconnues.push(commande);
        }
        for (const erreur of analyse.erreurs)
            erreurs.push(`${erreur} — ${extrait(portion.source)}`);
    }
    erreurs.push(...anomalies);
    return { segments, dessines, commandesInconnues, erreurs: [...new Set(erreurs)] };
}
//# sourceMappingURL=latex.js.map